import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, User, Mail, Phone, MapPin, Briefcase, BadgeCheck, Camera, Loader2, Droplets, HeartPulse, LogOut } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type SettingsRole = "patient" | "dietitian" | "doctor" | "admin";

interface SettingsPageProps {
  role: SettingsRole;
}

type SettingsForm = {
  full_name: string;
  email: string;
  phone: string;
  address: string;
  bio: string;
  age: string;
  gender: string;
  height: string;
  weight: string;
  blood_sugar: string;
  health_profile: string;
  qualification: string;
  specialization: string;
  experience: string;
  fee: string;
  department: string;
  access_level: string;
  profile_photo: string;
};

const emptyForm: SettingsForm = {
  full_name: "", email: "", phone: "", address: "", bio: "",
  age: "", gender: "", height: "", weight: "", blood_sugar: "",
  health_profile: "", qualification: "", specialization: "",
  experience: "", fee: "", department: "", access_level: "", profile_photo: "",
};

const SettingsPage = ({ role }: SettingsPageProps) => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const bn = language === "bn";
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const roleLabel = useMemo(() => {
    if (role === "patient") return bn ? "রোগী" : "Patient";
    if (role === "dietitian") return bn ? "ডায়েটিশিয়ান" : "Dietitian";
    if (role === "doctor") return bn ? "ডাক্তার" : "Doctor";
    return bn ? "অ্যাডমিন" : "Admin";
  }, [bn, role]);

  const [form, setForm] = useState<SettingsForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase.from("profiles").select("*").eq("user_id", user.id).single();
      if (data) {
        setForm({
          full_name: data.full_name || "",
          email: data.email || "",
          phone: data.phone || "",
          address: data.address || "",
          bio: data.bio || "",
          age: data.age ? String(data.age) : "",
          gender: data.gender || "",
          height: data.height ? String(data.height) : "",
          weight: data.weight ? String(data.weight) : "",
          blood_sugar: data.blood_sugar ? String(data.blood_sugar) : "",
          health_profile: data.health_profile || "",
          qualification: data.qualification || "",
          specialization: data.specialization || "",
          experience: data.experience ? String(data.experience) : "",
          fee: data.fee ? String(data.fee) : "",
          department: data.department || "",
          access_level: data.access_level || "",
          profile_photo: data.profile_photo || "",
        });
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const update = (key: keyof SettingsForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update("profile_photo", reader.result as string);
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const updateData: Record<string, any> = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        bio: form.bio,
        profile_photo: form.profile_photo,
      };
      if (role === "patient") {
        updateData.age = form.age ? parseInt(form.age) : null;
        updateData.gender = form.gender || null;
        updateData.height = form.height ? parseFloat(form.height) : null;
        updateData.weight = form.weight ? parseFloat(form.weight) : null;
        updateData.blood_sugar = form.blood_sugar ? parseFloat(form.blood_sugar) : null;
        updateData.health_profile = form.health_profile || null;
      }
      if (role === "dietitian" || role === "doctor") {
        updateData.qualification = form.qualification;
        updateData.specialization = form.specialization;
        updateData.experience = form.experience ? parseInt(form.experience) : null;
        updateData.fee = form.fee ? parseFloat(form.fee) : null;
      }
      if (role === "admin") {
        updateData.department = form.department;
        updateData.access_level = form.access_level;
      }

      const { error } = await supabase.from("profiles").update(updateData).eq("user_id", user.id);
      if (error) throw error;
      toast({ title: bn ? "সেটিংস সেভ হয়েছে" : "Settings saved", description: bn ? "আপনার প্রোফাইল আপডেট করা হয়েছে।" : "Your profile information was updated." });
    } catch {
      toast({ title: bn ? "সেভ ব্যর্থ" : "Save failed", description: bn ? "পরে আবার চেষ্টা করুন।" : "Please try again later.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const inputClass = "w-full rounded-full border border-[#dbe9e4] bg-[#f4faf7] px-4 py-3 text-sm font-medium text-foreground outline-none focus:border-[#5ba79d] focus:ring-0";
  const iconInputClass = "w-full rounded-full border border-[#dbe9e4] bg-[#f4faf7] py-3 pl-10 pr-4 text-sm font-medium text-foreground outline-none focus:border-[#5ba79d] focus:ring-0";
  const isPractitioner = role === "doctor" || role === "dietitian";

  return (
    <div className={`min-h-screen bg-[#f7faf8] text-[#245853] ${role === "patient" ? "pb-36" : "pb-12"}`}>
      <header className="relative overflow-hidden rounded-b-[30px] bg-[#edf7f3] px-5 pb-9 pt-7">
        <div className="relative z-10 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#dff1eb] text-[#347f78]"
            aria-label={bn ? "ফিরে যান" : "Go back"}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>
        <div className="relative z-10 mt-5 max-w-[58%]">
          <h1 className="font-serif text-[25px] font-extrabold leading-tight text-[#20534f]">
            {bn ? `${roleLabel} প্রোফাইল সম্পাদনা` : `Edit ${roleLabel} Profile`}
          </h1>
          <p className="mt-1 text-sm text-[#6d7978]">{bn ? "প্রোফাইল তথ্য সম্পাদনা করুন" : "Edit profile information"}</p>
        </div>
        <img src="/profile-edit.png" alt="" aria-hidden="true" className="absolute -bottom-1 right-1 h-[160px] w-[190px] object-contain" />
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#398b82]" /></div>
      ) : (
        <motion.div
          className={`relative z-10 ${isPractitioner ? "mx-0" : "mx-5"} -mt-1`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className={`soft-surface space-y-5 border border-[#e2ebe7] ${isPractitioner ? "rounded-[22px] p-4 sm:p-6" : "rounded-[26px] p-5"}`}>
            {isPractitioner && (
              <div className="flex items-center gap-3 border-b border-[#e2ebe7] pb-4">
                <div className="relative">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-[#cfe3dd] bg-[#e4f1eb]">
                    {form.profile_photo ? <img src={form.profile_photo} alt="Profile" className="h-full w-full object-cover" /> : <User className="h-7 w-7 text-[#398b82]" />}
                  </div>
                  <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#398b82] text-white">
                    <Camera className="h-3 w-3" />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                </div>
                <p className="text-xs text-[#6d7978]">{bn ? "প্রোফাইল ছবি আপলোড করুন" : "Upload profile photo"}</p>
              </div>
            )}

            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e1f2ed]">
                  <User className="h-4 w-4 text-[#398b82]" />
                </span>
                {bn ? "ব্যক্তিগত তথ্য" : "Personal Information"}
              </h3>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "পুরো নাম" : "Full Name"}</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#398b82]" />
                  <input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={iconInputClass} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-[#536462]">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#398b82]" />
                    <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={iconInputClass} />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "ফোন" : "Phone"}</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#398b82]" />
                    <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={iconInputClass} />
                  </div>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "ঠিকানা" : "Address"}</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#398b82]" />
                  <input value={form.address} onChange={(e) => update("address", e.target.value)} className={iconInputClass} />
                </div>
              </div>
            </div>

            {/* Patient: Physical + Health Details */}
            {role === "patient" && (
              <>
                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e1f2ed]">
                      <HeartPulse className="h-4 w-4 text-[#398b82]" />
                    </span>
                    {bn ? "শারীরিক তথ্য" : "Physical Details"}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                    <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "বয়স" : "Age"}</label>
                      <input placeholder={bn ? "বছর" : "Years"} value={form.age} onChange={(e) => update("age", e.target.value)} className={inputClass} />
                    </div>
                    <div>
                    <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "লিঙ্গ" : "Gender"}</label>
                      <select value={form.gender} onChange={(e) => update("gender", e.target.value)} className={inputClass}>
                        <option value="">{bn ? "বেছে নিন" : "Select"}</option>
                        <option value="Male">{bn ? "পুরুষ" : "Male"}</option>
                        <option value="Female">{bn ? "মহিলা" : "Female"}</option>
                        <option value="Other">{bn ? "অন্যান্য" : "Other"}</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                    <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "উচ্চতা (সেমি)" : "Height (cm)"}</label>
                      <input placeholder="cm" value={form.height} onChange={(e) => update("height", e.target.value)} className={inputClass} />
                    </div>
                    <div>
                    <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "ওজন (কেজি)" : "Weight (kg)"}</label>
                      <input placeholder="kg" value={form.weight} onChange={(e) => update("weight", e.target.value)} className={inputClass} />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e1f2ed]">
                      <Droplets className="h-4 w-4 text-[#398b82]" />
                    </span>
                    {bn ? "স্বাস্থ্য তথ্য" : "Health Details"}
                  </h3>
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-[#536462]">
                      {bn ? "রক্তের শর্করা (mg/dL)" : "Blood Glucose (mg/dL)"}
                    </label>
                    <input
                      type="number"
                      placeholder={bn ? "যেমন: 110" : "e.g. 110"}
                      value={form.blood_sugar}
                      onChange={(e) => update("blood_sugar", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "স্বাস্থ্য সমস্যা / রোগ" : "Health Conditions / Diagnosis"}</label>
                    <textarea
                      rows={3}
                      placeholder={bn ? "যেমন: ডায়াবেটিস, উচ্চ রক্তচাপ..." : "e.g. Diabetes Type 2, Hypertension..."}
                      value={form.health_profile}
                      onChange={(e) => update("health_profile", e.target.value)}
                      className="w-full resize-none rounded-2xl border border-[#dbe9e4] bg-[#f4faf7] px-4 py-3 text-sm font-medium text-foreground outline-none placeholder:text-[#7b8584] focus:border-[#5ba79d] focus:ring-0"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Doctor / Dietitian: Speciality */}
            {(role === "dietitian" || role === "doctor") && (
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e1f2ed]">
                    <BadgeCheck className="h-4 w-4 text-[#398b82]" />
                  </span>
                  {bn ? "বিশেষত্ব" : "Speciality"}
                </h3>
                <div><label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "যোগ্যতা" : "Qualification"}</label><input value={form.qualification} onChange={(e) => update("qualification", e.target.value)} className={inputClass} /></div>
                <div><label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "বিশেষত্ব" : "Specialization"}</label><input value={form.specialization} onChange={(e) => update("specialization", e.target.value)} className={inputClass} /></div>
                <div><label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "অভিজ্ঞতা (বছর)" : "Experience (years)"}</label><input value={form.experience} onChange={(e) => update("experience", e.target.value)} className={inputClass} /></div>
              </div>
            )}

            {(role === "dietitian" || role === "doctor") && (
              <div className="space-y-4">
                <h3 className="border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">{bn ? "পরামর্শ ফি" : "Consultation Fees"}</h3>
                <div><label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "ফি (৳)" : "Fee (৳)"}</label><input value={form.fee} onChange={(e) => update("fee", e.target.value)} className={inputClass} /></div>
              </div>
            )}

            {/* Admin */}
            {role === "admin" && (
              <div className="space-y-4">
                <h3 className="border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">{bn ? "অ্যাডমিন তথ্য" : "Admin Details"}</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="relative"><Briefcase className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#398b82]" /><input value={form.department} onChange={(e) => update("department", e.target.value)} placeholder={bn ? "বিভাগ" : "Department"} className={iconInputClass} /></div>
                  <div className="relative"><BadgeCheck className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#398b82]" /><input value={form.access_level} onChange={(e) => update("access_level", e.target.value)} placeholder={bn ? "অ্যাক্সেস লেভেল" : "Access Level"} className={iconInputClass} /></div>
                </div>
              </div>
            )}

            {/* Bio for doctors/dietitians */}
            {(role === "doctor" || role === "dietitian") && (
              <div className="space-y-4">
                <h3 className="border-b border-[#e2ebe7] pb-2 text-xs font-extrabold uppercase tracking-wide text-[#245853]">{bn ? "সংক্ষিপ্ত পরিচিতি" : "Short Bio"}</h3>
                <textarea rows={3} value={form.bio} onChange={(e) => update("bio", e.target.value)} className="w-full resize-none rounded-2xl border border-[#dbe9e4] bg-[#f4faf7] px-4 py-3 text-sm font-medium text-foreground outline-none focus:border-[#5ba79d] focus:ring-0" />
              </div>
            )}

            <button onClick={saveProfile} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#398b82] py-3.5 text-sm font-extrabold text-white transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {bn ? "পরিবর্তন সংরক্ষণ" : "Save Changes"}
            </button>
            {isPractitioner && (
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-[#e7cfcf] bg-[#fff8f8] py-3 text-sm font-extrabold text-[#b35f5f] transition-colors hover:bg-[#fff1f1]"
              >
                <LogOut className="h-4 w-4" />
                {bn ? "লগআউট" : "Log out"}
              </button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SettingsPage;
